import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CloseBold, Search16 } from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Dropdown } from '@elfsight/widget-modal';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { Button, Icon, Loader, TextControl } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { useActiveLocation } from '../active-location-context';
import { useUserLocation } from '../user-location-context';
import { processSearchResults } from './process-search-results';
import { SearchResultItem } from './search-result-item';
import { useGeocodingSearch } from './use-geocoding-search';

const LIGHT_BG_COLOR = 'rgba(255, 255, 255, 1)';
const DARK_BG_COLOR = 'rgba(64, 64, 64, 1)';

export const DirectorySearch = () => {
  const {
    textColor,
    accentColor,
    elementsCornerRadius,
    locations = [],
    demoLocations,
    editMode
  } = useSettings();
  const { t } = useLingui();
  const mutedColor10 = getColorWithAlpha(textColor, 0.1);
  const mutedColor = getColorWithAlpha(textColor, 0.5);
  const mutedColor70 = getColorWithAlpha(textColor, 0.7);
  const iconColor = getColorWithAlpha(textColor, 0.3);
  const backgroundColor = getColorWithAlpha(textColor, 0.05);
  const dropdownBackgroundColor = getInvertedColor(textColor, [
    LIGHT_BG_COLOR,
    DARK_BG_COLOR
  ]);
  const scrollbarColor =
    dropdownBackgroundColor === LIGHT_BG_COLOR ? DARK_BG_COLOR : LIGHT_BG_COLOR;

  const [anchorElement, setAnchorElement] = useState<Element | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { setActiveLocation } = useActiveLocation();

  const availableDemoLocation = editMode ? demoLocations : [];
  const widgetLocation = locations.length ? locations : availableDemoLocation;

  const [width, setWidth] = useState(0);
  const [measureRef] = useRect(({ width }) => setWidth(width), {
    resize: true
  });
  const inputRef = useRef<TextControlBaseInstance>(null);
  const mergedRef = useMergedRef(setAnchorElement, measureRef);

  const { data: searchResults = [], isFetching } = useGeocodingSearch({
    query,
    enabled: dropdownOpen && query.length > 1
  });

  const { userLocation, setUserLocation, refreshUserPosition } =
    useUserLocation();

  const handleSearch = () => {
    if (query.length) {
      setDropdownOpen(true);
    } else {
      inputRef?.current?.focusInput();
    }
  };

  const handleClearSearch = () => {
    setQuery('');
    refreshUserPosition();
    setDropdownOpen(false);
  };

  useEffect(() => {
    if (userLocation?.address) {
      setQuery(userLocation.address);
    }
  }, [userLocation]);

  const handleSelectResult = ({
    coordinates,
    query: newQuery,
    locationId
  }: {
    coordinates: { lng: number; lat: number };
    query: string;
    locationId?: string;
  }) => {
    setUserLocation(coordinates);
    setQuery(newQuery);
    if (locationId) {
      setActiveLocation(locationId);
    }
  };

  useEffect(() => {
    if (!query) {
      refreshUserPosition();
    }
  }, [query]);

  const renderDropdown = () => {
    const searchFinal = processSearchResults(
      searchResults,
      widgetLocation,
      query
    );

    if (!searchFinal.length && isFetching) {
      return <Loader size={24} color={mutedColor} />;
    }

    if (query.length && searchFinal.length) {
      return (
        <DropdownScrollWrapper $scrollbarColor={scrollbarColor}>
          {searchFinal.map((result, index) => (
            <SearchResultItem
              key={`${result.id || result.placeId}_${index}`}
              {...result}
              textColor={textColor}
              mutedColor={mutedColor}
              backgroundColor={backgroundColor}
              onSelect={handleSelectResult}
            />
          ))}
          {isFetching && (
            <Loader
              style={{ marginTop: 6, marginBottom: 6 }}
              size={24}
              color={mutedColor}
            />
          )}
        </DropdownScrollWrapper>
      );
    } else {
      return (
        <DropdownEmpty $color={mutedColor}>
          {t`No results found.`}
        </DropdownEmpty>
      );
    }
  };

  const placeholder = t`Search by address, ZIP, or name`;

  return (
    <Container ref={mergedRef}>
      <StyledTextControl
        ref={inputRef}
        name="search"
        ariaLabel={placeholder}
        placeholder={placeholder}
        placeholderColor={mutedColor}
        borderRadius={elementsCornerRadius === 'rounded' ? 8 : 100}
        value={query}
        onValueChange={setQuery}
        font={{ fontSize: 14 }}
        color={textColor}
        background={backgroundColor}
        lineHeight={1.7}
        onKeyUp={({ key }: KeyboardEvent) => {
          if (key === 'Enter') {
            handleSearch();
          }
        }}
        leftSlot={<Icon component={Search16} size={16} fill={iconColor} />}
        rightSlot={
          <RightSlotWrapper style={{ marginRight: -10 }}>
            {query && (
              <ClearButton
                $background={mutedColor10}
                onClick={handleClearSearch}
                $color={mutedColor70}
              />
            )}
            <Button
              color={accentColor}
              fontColor={getInvertedColor(accentColor)}
              onClick={handleSearch}
              borderRadius={elementsCornerRadius === 'rounded' ? 4 : 100}
              verticalPadding={8}
              font={{
                fontWeight: 'bold',
                fontSize: 13
              }}
            >
              {t`Search`}
            </Button>
          </RightSlotWrapper>
        }
        $accentColor={accentColor}
      />

      <StyledDropdown
        open={dropdownOpen}
        width={`${width}px`}
        background={dropdownBackgroundColor}
        displayOver={false}
        anchorElement={anchorElement}
        onCloseRequest={() => setDropdownOpen(false)}
        portalContainerElement={anchorElement ?? undefined}
        $borderRadius={elementsCornerRadius === 'rounded' ? 8 : 20}
      >
        {renderDropdown()}
      </StyledDropdown>
    </Container>
  );
};

const Container = styled.div``;

const StyledDropdown = styled(Dropdown)<{
  $borderRadius: number;
}>`
  border-radius: ${({ $borderRadius }) => $borderRadius}px;
  overflow: hidden;
`;

const DropdownScrollWrapper = styled.div<{
  $scrollbarColor?: string;
  $disableScroll?: boolean;
}>`
  max-height: 235px;
  overflow-y: auto;
  border-radius: 20px;

  scrollbar-width: auto;

  @supports (-moz-appearance: none) {
    scrollbar-width: thin;
    scrollbar-color: ${({ $scrollbarColor }) =>
        getColorWithAlpha($scrollbarColor ?? '#000', 0.1)}
      transparent;
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background-color: ${({ $scrollbarColor }) =>
      getColorWithAlpha($scrollbarColor ?? '#000', 0.1)};

    &:hover {
      background-color: ${({ $scrollbarColor }) =>
        getColorWithAlpha($scrollbarColor ?? '#000', 0.15)};
    }
  }
`;

const DropdownEmpty = styled.div<{ $color?: string }>`
  padding: 6px 16px;
  color: ${({ $color }) => $color};
`;

const StyledTextControl = styled(TextControl)<{
  $accentColor: string;
}>`
  &:focus-within {
    border-color: ${({ $accentColor }) => $accentColor}!important;
  }

  transition: border-color 0.2s ease;
`;

const RightSlotWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClearButton = styled(CloseBold)<{
  $color: string;
  $background: string;
}>`
  ${({ $color, $background }) => css`
    cursor: pointer;
    width: 16px;
    padding: 4px;
    height: 16px;
    border-radius: 50%;
    border: none;
    fill: ${$color};
    background: ${$background};
  `}
`;
